import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Button } from "antd";
import { useAppContext } from "../AppContext";
import TermsAndCondition from "./TermsAndCondition";
import { Helmet } from "react-helmet-async";

const API_BASE = "https://app.commercialcleaningleadsforyou.com";

export default function DownloadLeadsPage() {
  const { paymentId, email, zipcodes } = useAppContext();
  const navigate = useNavigate();
  const [loadings, setLoadings] = useState(false);
  const [downloadAccepted, setDownloadAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  useEffect(() => {
    if (!paymentId) {
      navigate("/", { replace: true });
    }
  }, [paymentId, navigate]);

  async function postDownloadStatus(pid, status) {
    if (!pid) return;
    try {
      await fetch(`${API_BASE}/api/update-download-status-newBusiness`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ paymentId: pid, downloadStatus: status }),
      });
    } catch (e) {
      console.warn("update-download-status failed:", e);
    }
  }

  const getFilenameFromHeaders = (resp) => {
    const cd = resp.headers.get("content-disposition");
    if (!cd) return null;
    const m = /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i.exec(cd);
    return decodeURIComponent(m?.[1] || m?.[2] || "");
  };

  const handleDownload = async (e) => {
    e.preventDefault();
    if (!paymentId) return;

    setLoadings(true);
    let statusPosted = false;
    const postOnce = async (status) => {
      if (statusPosted) return;
      statusPosted = true;
      await postDownloadStatus(paymentId, status);
    };

    try {
      const resp = await fetch(`${API_BASE}/api/newBusiness-download-csv`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          zipcode: zipcodes,
          email,
          paymentId,
        }),
      });

      if (!resp.ok) {
        await postOnce("No");
        const text = await resp.text();
        let message = text;
        try {
          message = JSON.parse(text).error || message;
        } catch {}
        throw new Error(message || `Request failed: ${resp.status}`);
      }

      await postOnce("Yes");

      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const now = new Date();
      const monthNames = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December",
      ];
      const monthYear = `${monthNames[now.getMonth()]}_${now.getFullYear()}`;
      const filename =
        getFilenameFromHeaders(resp) ||
        `new-business-${(zipcodes || "data").replace(/[^\w\s-]/g, "")}_${monthYear}.csv`;

      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      navigate("/thank-you");
    } catch (err) {
      console.error(err);
      await postOnce("No");
      alert(err?.message || "Error downloading file");
    } finally {
      setLoadings(false);
    }
  };

  const handleDownloadAgree = () => {
    setDownloadAccepted(true);
    setShowTerms(false);
  };

  if (!paymentId) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>Download Your Leads | New Business Leads</title>
      </Helmet>
      <div className="min-h-screen bg-[#F8F8F8] flex items-center justify-center p-4">
        <div className="w-full max-w-lg mx-auto">
          <div className="shadow-2xl border-4 border-[#F09900] rounded-2xl overflow-hidden bg-white">
            <div className="min-h-[24rem] p-8 flex flex-col items-center mt-3 text-center gap-5">
              <div className="relative">
                <span className="absolute inset-0 rounded-full animate-ping bg-orange-400/40" />
                <div className="relative z-10 grid h-16 w-16 place-items-center rounded-full bg-orange-100 text-[#F09900]">
                  <svg
                    className="h-8 w-8"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </div>
              </div>

              <p className="text-2xl font-bold text-[#F09900] mt-6">
                Payment Successful!
              </p>
              <p className="max-w-sm text-[#4D6877] font-semibold text-lg">
                Thanks for your purchase! Your New Business Leads file is ready to download.
                We’ve also sent a confirmation email with your receipt and download link.
              </p>

              <div className="mt-2 w-full max-w-sm">
                {/* <div className="flex items-center gap-3 ml-1 mb-4 justify-start">
                  <input
                    id="accept-download"
                    type="checkbox"
                    checked={downloadAccepted}
                    onChange={(e) => setDownloadAccepted(e.target.checked)}
                    className="h-4 w-4 cursor-pointer rounded border-slate-600 bg-slate-900 text-indigo-600 focus:ring-indigo-500"
                    aria-describedby="terms-help"
                  />
                  <label
                    htmlFor="accept-download"
                    className="text-sm text-gray-700 leading-relaxed"
                  >
                    I've read and accept the{" "}
                    <button
                      type="button"
                      className="font-medium underline-offset-2 underline cursor-pointer"
                      onClick={() => setShowTerms(true)}
                      aria-label="Read the full Terms and Conditions"
                      style={{ color: "blue" }}
                    >
                      Terms & Conditions
                    </button>
                  </label>
                </div> */}
                <button
                  onClick={handleDownload}
                  aria-busy={loadings ? "true" : "false"}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#F09900] px-9 py-4 font-semibold text-white shadow transition hover:bg-[#E08900] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F09900] focus-visible:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer"
                >
                  {loadings ? (
                    <>
                      <span
                        className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"
                        aria-hidden="true"
                      />
                      Preparing download…
                    </>
                  ) : (
                    "Download now"
                  )}
                </button>
              </div>

              {loadings && (
                <div className="mt-4 w-full max-w-sm h-1.5 overflow-hidden rounded-full bg-slate-200">
                  <div className="h-full w-1/2 animate-pulse rounded-full bg-[#F09900]" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        title="Terms & Conditions"
        open={showTerms}
        centered
        onCancel={() => setShowTerms(false)}
        footer={[
          <div key="footer" className="my-5 mx-5 gap-12 flex justify-center">
            <Button
              key="agree"
              type="primary"
              onClick={handleDownloadAgree}
              className="!h-10 w-32 !rounded-full mr-4 text-base"
              style={{
                backgroundColor: "#F09900",
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              Agree
            </Button>
            <Button
              key="cancel"
              onClick={() => setShowTerms(false)}
              className="!h-10 w-32 !rounded-full bg-red-400 text-white"
              style={{
                backgroundColor: "red",
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              Cancel
            </Button>
          </div>,
        ]}
        width={720}
      >
        <TermsAndCondition />
      </Modal>
    </>
  );
}
